import { DEFAULT_ERROR_TEXT } from '@utils';

import { isEmpty } from './validationUtils';

import FormattingService from 'src/core/services/FormattingService';

export const enumListAsRecordObject = (
  enumList: { __typename?: string; name: string }[],
  addValue = false,
  includeSlash = false
): Record<string, string> => {
  const dropDownList: Record<string, string> = {};
  enumList?.forEach((element: { __typename: string; name: string }) => {
    if (element.name) {
      const displayName = FormattingService.formatEnumToDisplay(
        element.name,
        includeSlash
      );

      dropDownList[element.name] = displayName;
      if (addValue) {
        dropDownList[displayName] = element.name;
      }
    }
  });

  return dropDownList;
};

// keeps existing usage working
export function lookupListAsRecordObject(
  lookupList: { [key: string]: string }[],
  showCodePrefix?: boolean,
  key?: string,
  title?: string,
  code?: string,
  prefixNumericKeys?: boolean
): Record<string, string>;

// for typed arrays
export function lookupListAsRecordObject<T extends object>(
  lookupList: T[],
  showCodePrefix?: boolean,
  key?: keyof T & string,
  title?: keyof T & string,
  code?: keyof T & string,
  prefixNumericKeys?: boolean
): Record<string, string>;

export function lookupListAsRecordObject(
  lookupList: any[],
  showCodePrefix = false,
  key = 'id',
  title = 'title',
  code = 'code',
  prefixNumericKeys = false
): Record<string, string> {
  if (!Array.isArray(lookupList) || lookupList.length === 0) {
    return {};
  }

  const result: Record<string, string> = {};

  for (const element of lookupList) {
    const k = element?.[key];
    const t = element?.[title];
    if (k == null || t == null) continue;

    const isNumeric = /^\d+$/.test(String(k));
    const safeKey =
      isNumeric && prefixNumericKeys ? `_${String(k)}` : String(k);

    result[safeKey] =
      showCodePrefix && element?.[code] != null
        ? `${String(element[code])} - ${String(t)}`
        : String(t);
  }

  return result;
}

export const getError = (response?: any): string => {
  const errors = response?.errors || response?.graphQLErrors;

  if (!errors || isEmpty(errors)) {
    return DEFAULT_ERROR_TEXT;
  }
  const extractedMessages = errors
    .map((err: any) => {
      const rawMessage = err?.message || '';

      const match = rawMessage.match(/Message:\s*(.*?)\s*(,|$)/);
      if (match && match[1]) {
        return match[1].trim();
      }

      return rawMessage.trim();
    })
    .filter((msg: string) => !!msg);

  return extractedMessages.length > 0
    ? extractedMessages.join('\n')
    : DEFAULT_ERROR_TEXT;
};

export const lookupListAsRecordObjectWithArabicTitle = (
  lookupList: { [key: string]: string }[],
  key = 'Id',
  title = 'Title',
  titleAr = 'TitleAr'
): Record<string, string> => {
  const lookuplistAsRecordObject: Record<string, string> = {};

  lookupList.forEach((element: { [key: string]: string }) => {
    if (element[key] && element[title]) {
      lookuplistAsRecordObject[element[key]] =
        element[title] + ' - ' + element[titleAr];
    }
  });

  return lookuplistAsRecordObject;
};
